// EPI 查询服务类型 SectionFunction
// 由于 Ico 字段是 SVG 图标 (byte[])，Create/Edit 必须走 FormData（multipart/form-data），
// 因此覆盖 Common 的 editBySection / customFormOnSubmit / cancelCustomForm，
// 让其与 JobStatusStepper 的图标上传模式一致。
import { config } from 'util/config';
import { httpClient, uploadClient } from "util/Api";
import * as globalFunction from 'util/GlobalFunction';
import { message } from 'antd';

// ---------------- 管理 Group 弹窗 ----------------
// 新版 XML：section 是单 ServiceRequestTypeTable（非 TabSection 包裹）
// topButton 注入的 sectionComponent = DefaultSection，真正的 ServiceRequestTypeTable = sectionComponent.child.current
export function manageServiceRequestTypeGroup({ sectionComponent } = {}) {
  const table = sectionComponent.child?.current;
  if (!table) return;
  table.setState({ manageGroupShow: true });
  setTimeout(() => {
    table.manageGroup?.current?.getServiceRequestTypeGroupList();
  }, config.setTimeoutMilliSecond);
}

// ---------------- 管理 ServiceRequestTypeRequiredDocumentType 弹窗 ----------------
// rowButton 触发：parameter = 行 record (含 key / Name / AuthorityType 等)
// sectionComponent = DefaultSection；ServiceRequestTypeTable = sectionComponent.child.current
export function manageRequiredDocuments({ parameter, sectionComponent } = {}) {
  const table = sectionComponent.child?.current;
  if (!table) return;
  table.setState({
    requiredDocumentsShow: true,
    requiredDocumentsServiceRequestType: parameter,
  });
}

// ---------------- 添加 (在某个 Group 的 “+” 按钮里调用) ----------------
// 传入 parameter = { ServiceRequestTypeGroupId } 预填分组
export function addServiceRequestType({ parameter, sectionComponent, pageComponent } = {}) {
  sectionComponent.setState({ showModal: true, componentType: 'Create' });
  setTimeout(() => {
    let formData = parameter || {};
    sectionComponent.customForm.current?.formRef.current?.setFieldsValue(formData);
    sectionComponent.customForm.current?.setState({ formData, currentDataKey: 0 });
    // 重置图标 (新增时无图)
    sectionComponent.customForm.current?.additionalFormRef?.current?.child?.current?.setState({
      icoFileList: [], icoPreview: null,
    });
  }, config.setTimeoutMilliSecond);
}

// ---------------- 编辑 (覆盖 Common.editBySection) ----------------
// 必须自己拉数据：除了普通字段，还要把 Ico (base64 string) 还原成 File 塞进 Dragger
export function editBySection({ parameter, sectionComponent, pageComponent, sectionName } = {}) {
  sectionComponent.setState({ dataLoading: true, showModal: true, componentType: 'Edit' });
  setTimeout(() => {
    sectionComponent.customForm.current?.setState({ autoLoadComponentDataSource: false });
    sectionComponent.customForm.current?.getComponentDataSource();

    httpClient
      .get(pageComponent.props.areaName + '/' + sectionName + '/' + parameter.key)
      .then(({ data }) => {
        if (!data.result) {
          globalFunction.handleResultError(data, pageComponent);
          return;
        }
        // 普通字段直接 setFieldsValue
        sectionComponent.customForm.current?.formRef.current?.setFieldsValue({
          ServiceRequestTypeGroupId: data.data.ServiceRequestTypeGroupId,
          Name: data.data.Name,
          AuthorityType: data.data.AuthorityType,
          ServiceCharge: data.data.ServiceCharge,
          DefaultAuthorityCharge: data.data.DefaultAuthorityCharge,
          AccountCode: data.data.AccountCode,
          Tax: data.data.Tax,
          DaysLowerBound: data.data.DaysLowerBound,
          DaysUpperBound: data.data.DaysUpperBound,
          State: data.data.State,
          Description: data.data.Description,
          key: data.data.key,
        });
        sectionComponent.customForm.current?.setState({ formData: data.data, currentDataKey: data.data.key });

        // Ico (byte[] → base64) 还原为 File 注入 Dragger（仿 JobStatusStepper）
        let icoFileList = [];
        if (data.data.Ico !== null && data.data.Ico !== undefined) {
          let icoString = window.atob(data.data.Ico);
          let len = icoString.length;
          let bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = icoString.charCodeAt(i);
          }
          let blob = new Blob([bytes], { type: 'application/octet-stream' });
          let file = new File([blob], 'icon.svg', { type: 'application/octet-stream', lastModified: Date.now() });
          icoFileList.push(file);
        }
        setTimeout(() => {
          if (sectionComponent.customForm.current?.additionalFormRef?.current) {
            sectionComponent.customForm.current.additionalFormRef.current.child?.current?.setState({
              icoFileList,
              icoPreview: data.data.Ico || null,
            });
          }
          sectionComponent.setState({ dataLoading: false, isFormModified: false });
        }, config.setTimeoutMilliSecond);
      })
      .catch(function (error) {
        globalFunction.handleError(error, pageComponent);
      });
  }, config.setTimeoutMilliSecond);
}

// ---------------- 提交 (Create/Edit 都走 FormData) ----------------
// 覆盖 Common.customFormOnSubmit。后端 ServiceRequestTypeController 改为 [FromForm]+IFormCollection。
export function customFormOnSubmit({ sectionComponent, pageComponent } = {}) {
  let values = sectionComponent.customForm.current?.formRef.current?.getFieldsValue();

  // Service Types 专属校验：起始天数不得大于结束天数，避免影响其他 Section 的通用表单规则。
  const daysLowerBound = Number(values?.DaysLowerBound);
  const daysUpperBound = Number(values?.DaysUpperBound);
  if (Number.isFinite(daysLowerBound) && Number.isFinite(daysUpperBound) && daysLowerBound > daysUpperBound) {
    const validationMessage = 'Timeframe start must be less than or equal to the end.';
    sectionComponent.customForm.current?.formRef.current?.setFields([
      { name: 'DaysUpperBound', errors: [''] },
    ]);
    message.error(validationMessage);
    return;
  }

  sectionComponent.setState({ customFormSubmitting: true });
  let formData = new FormData();

  for (let key in values) {
    if (values[key] === undefined || values[key] === null) continue;
    if (key === 'Ico') continue; // Ico 单独 append（来自 Dragger 的 File）
    formData.append(key, values[key]);
  }

  // 追加 Ico 文件 (如果用户选了)
  let icoFileList = sectionComponent.customForm.current?.additionalFormRef?.current?.child?.current?.state?.icoFileList || [];
  icoFileList.forEach((file) => {
    formData.append('Ico', file);
  });

  const isCreate = sectionComponent.state.componentType === 'Create';
  if (!isCreate) {
    formData.append('key', sectionComponent.customForm.current?.state.currentDataKey);
    formData.append('CompanyId', sectionComponent.props.companyId);
  }

  const request = isCreate
    ? uploadClient.post('Admin/ServiceRequestType', formData)
    : uploadClient.put('Admin/ServiceRequestType', formData);

  request
    .then(({ data }) => {
      if (data.result) {
        sectionComponent.setState({ showModal: false, customFormSubmitting: false });
        setTimeout(() => {
          // 清表单 + 清图标
          sectionComponent.customForm.current?.formRef.current?.resetFields();
          sectionComponent.customForm.current?.additionalFormRef?.current?.child?.current?.setState({
            icoFileList: [], icoPreview: null,
          });
          // 刷新分组列表
          sectionComponent.getSectionDataByDefault();
          setTimeout(() => {
            sectionComponent.setState({ componentType: null });
          }, config.setTimeoutMilliSecond);
        }, config.setTimeoutMilliSecond);
      } else {
        globalFunction.handleResultError(data, pageComponent);
        sectionComponent.setState({ customFormSubmitting: false });
      }
    })
    .catch(function (error) {
      globalFunction.handleError(error, pageComponent);
      sectionComponent.setState({ customFormSubmitting: false });
    });
}

// ---------------- 取消弹窗 (清空图标) ----------------
export function cancelCustomForm({ sectionComponent } = {}) {
  sectionComponent.setState({ showModal: false, customFormSubmitting: false, isFormModified: false });
  setTimeout(() => {
    sectionComponent.customForm.current?.resetFields();
    sectionComponent.customForm.current?.additionalFormRef?.current?.child?.current?.setState({
      icoFileList: [], icoPreview: null,
    });
    sectionComponent.setState({ componentType: null });
  }, config.setTimeoutMilliSecond);
}
